import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './ProblemTypeSelector.css';

export interface ProblemTypeSelectorProps {
  value: string | string[]; // Support both single and multi-select
  onChange: (problemType: string | string[]) => void;
  error?: string;
  multiSelect?: boolean;
  maxSelections?: number;
  showRecommended?: boolean;
  userSkills?: string[];
  userInterests?: string[];
}

const PROBLEM_TYPES = [
  {
    id: 'automation',
    title: 'Save Time & Automate',
    icon: '⚡',
    description: 'Automate repetitive tasks and workflows',
    examples: ['Workflow automation', 'Data processing', 'Task scheduling'],
    category: 'Efficiency'
  },
  {
    id: 'communication',
    title: 'Connect & Collaborate',
    icon: '💬',
    description: 'Improve team communication and collaboration',
    examples: ['Team messaging', 'Project coordination', 'Knowledge sharing'],
    category: 'Collaboration'
  },
  {
    id: 'data-insights',
    title: 'Analyze & Understand',
    icon: '📊',
    description: 'Turn data into actionable insights',
    examples: ['Dashboards', 'Performance tracking', 'Data visualization'],
    category: 'Analytics'
  },
  {
    id: 'content-creation',
    title: 'Create & Manage',
    icon: '🎨',
    description: 'Tools for content creation and management',
    examples: ['Content generation', 'Design tools', 'Media management'],
    category: 'Content'
  },
  {
    id: 'customer-experience',
    title: 'Serve Customers Better',
    icon: '❤️',
    description: 'Enhance customer satisfaction and support',
    examples: ['Support automation', 'Feedback collection', 'User onboarding'],
    category: 'Customer Success'
  },
  {
    id: 'business-operations',
    title: 'Run Business Smoothly',
    icon: '🏢',
    description: 'Streamline core business processes',
    examples: ['Inventory management', 'Financial tracking', 'HR processes'],
    category: 'Operations'
  },
  {
    id: 'learning-development',
    title: 'Learn & Grow',
    icon: '📚',
    description: 'Educational tools and skill development',
    examples: ['Training platforms', 'Skill assessment', 'Progress tracking', 'Certification tools'],
    category: 'Education'
  },
  {
    id: 'marketplace-platform',
    title: 'Marketplace & Platforms',
    icon: '🏪',
    description: 'Solutions that connect buyers and sellers or service providers',
    examples: ['Service marketplaces', 'Product platforms', 'Booking systems', 'Community tools'],
    category: 'Marketplaces'
  },
  {
    id: 'financial-management',
    title: 'Financial Solutions',
    icon: '💰',
    description: 'Tools for managing money and financial decisions',
    examples: ['Personal finance apps', 'Investment platforms', 'Expense tracking', 'Budget planning'],
    category: 'Finance'
  },
  {
    id: 'security-privacy',
    title: 'Security & Privacy',
    icon: '🔐',
    description: 'Protect data and ensure digital security',
    examples: ['Identity protection', 'Data security', 'Privacy tools', 'Authentication systems'],
    category: 'Security'
  },
  {
    id: 'sustainability-environment',
    title: 'Sustainability & Environment',
    icon: '🌱',
    description: 'Solutions for environmental and sustainability challenges',
    examples: ['Carbon tracking', 'Waste reduction', 'Green technology', 'Renewable energy tools'],
    category: 'Sustainability'
  },
  {
    id: 'social-community',
    title: 'Social & Community',
    icon: '👥',
    description: 'Build connections and foster communities',
    examples: ['Social networks', 'Community platforms', 'Event organization', 'Networking tools'],
    category: 'Social'
  }
];

const CATEGORIES = [...new Set(PROBLEM_TYPES.map(pt => pt.category))];

export const ProblemTypeSelector: React.FC<ProblemTypeSelectorProps> = ({
  value,
  onChange,
  error,
  multiSelect = false,
  maxSelections = 3,
  showRecommended = true,
  userSkills = [],
  userInterests = []
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [tooltip, setTooltip] = useState<{ visible: boolean; content: string; x: number; y: number }>({
    visible: false,
    content: '',
    x: 0,
    y: 0
  });
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Translation helper functions
  const getProblemTitle = (id: string) => {
    return t(`aiIdeaGenerator.problem_${id}_title`);
  };

  const getProblemDescription = (id: string) => {
    return t(`aiIdeaGenerator.problem_${id}_desc`);
  };

  const getProblemExample = (id: string, index: number) => {
    return t(`aiIdeaGenerator.problem_${id}_ex${index + 1}`);
  };

  const getCategoryName = (category: string) => {
    const categoryKey = category.replace(/\s+/g, '').replace(/&/g, '');
    return t(`aiIdeaGenerator.category${categoryKey}`);
  };

  // Normalize value to always be an array for easier processing
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
  
  // Get recommended problem types based on user skills/interests
  const recommendedTypes = useMemo(() => {
    if (!showRecommended || (!userSkills.length && !userInterests.length)) return [];
    
    const techSkills = ['JavaScript', 'Python', 'React', 'Node.js', 'API Development'];
    const hasTeachSkills = userSkills.some(skill => skill.includes('Teaching') || skill.includes('Education'));
    const hasDataSkills = userSkills.some(skill => skill.includes('Data') || skill.includes('Analytics'));
    const hasDesignSkills = userSkills.some(skill => skill.includes('Design') || skill.includes('UX'));
    
    const recommended = [];
    
    if (userSkills.some(skill => techSkills.includes(skill))) {
      recommended.push('automation', 'data-insights');
    }
    if (hasTeachSkills || userInterests.includes('Education')) {
      recommended.push('learning-development');
    }
    if (hasDataSkills || userInterests.includes('Analytics')) {
      recommended.push('data-insights');
    }
    if (hasDesignSkills || userInterests.includes('Design')) {
      recommended.push('content-creation');
    }
    if (userInterests.includes('E-commerce') || userInterests.includes('Business')) {
      recommended.push('marketplace-platform', 'business-operations');
    }
    
    return [...new Set(recommended)];
  }, [userSkills, userInterests, showRecommended]);

  const filteredProblemTypes = useMemo(() => {
    let filtered = PROBLEM_TYPES;
    
    if (activeCategory !== 'All') {
      if (activeCategory === 'Recommended') {
        filtered = filtered.filter(pt => recommendedTypes.includes(pt.id));
      } else {
        filtered = filtered.filter(pt => pt.category === activeCategory);
      }
    }
    
    if (searchTerm) {
      filtered = filtered.filter(pt =>
        pt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pt.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pt.examples.some(example => example.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pt.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [searchTerm, activeCategory, recommendedTypes]);

  const handleProblemTypeSelect = (problemType: string) => {
    if (multiSelect) {
      const newSelection = selectedValues.includes(problemType)
        ? selectedValues.filter(id => id !== problemType)
        : selectedValues.length < maxSelections
          ? [...selectedValues, problemType]
          : selectedValues;
      onChange(newSelection);
    } else {
      onChange(problemType);
    }
  };

  const handleRemoveChip = (problemType: string) => {
    if (multiSelect) {
      const newSelection = selectedValues.filter(id => id !== problemType);
      onChange(newSelection);
    }
  };

  const clearAllSelections = () => {
    onChange(multiSelect ? [] : '');
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const showTooltip = (content: string, event: React.MouseEvent) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      content,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const hideTooltip = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltip(prev => ({ ...prev, visible: false }));
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="problem-area-enhanced">
      {/* Header */}
      <div className="problem-area-header">
        <h3 className="problem-area-title">
          <span className="section-icon">🎯</span>
          {t('aiIdeaGenerator.whatProblemsToSolve')}
        </h3>
        <p className="problem-area-subtitle">
          {multiSelect 
            ? t('aiIdeaGenerator.chooseUpToProblems', { max: maxSelections })
            : t('aiIdeaGenerator.chooseAreaExcites')
          }
        </p>
        {error && <span className="error-text">{error}</span>}
      </div>

      {/* Selected Problems Chips */}
      {multiSelect && selectedValues.length > 0 && (
        <div className="selected-problems-chips">
          <span className="selected-problems-label">{t('aiIdeaGenerator.selected')}:</span>
          {selectedValues.map(problemId => {
            const problemType = PROBLEM_TYPES.find(pt => pt.id === problemId);
            return problemType ? (
              <div key={problemId} className="problem-chip">
                <span>{problemType.icon} {getProblemTitle(problemType.id)}</span>
                <button
                  type="button"
                  className="problem-chip-remove"
                  onClick={() => handleRemoveChip(problemId)}
                  aria-label={`${t('aiIdeaGenerator.remove')} ${getProblemTitle(problemType.id)}`}
                >
                  ×
                </button>
              </div>
            ) : null;
          })}
          {selectedValues.length > 0 && (
            <button
              type="button"
              onClick={clearAllSelections}
              className="quick-action-btn"
              style={{ marginLeft: 'auto' }}
            >
              {t('aiIdeaGenerator.clearAll')}
            </button>
          )}
        </div>
      )}

      {/* Enhanced Controls */}
      <div className="problem-area-controls">
        {/* Enhanced Search */}
        <div className="enhanced-search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder={t('aiIdeaGenerator.searchProblemsGlobally')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="enhanced-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={clearSearch}
              className="clear-search-enhanced"
              aria-label={t('common.clear')}
            >
              ×
            </button>
          )}
        </div>
        
        {/* Enhanced Category Tabs */}
        <div className="enhanced-category-tabs">
          <button
            type="button"
            onClick={() => setActiveCategory('All')}
            className={`enhanced-category-tab ${activeCategory === 'All' ? 'active' : ''}`}
          >
            {t('aiIdeaGenerator.allProblems')} <span className="category-count">{PROBLEM_TYPES.length}</span>
          </button>
          
          {showRecommended && recommendedTypes.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveCategory('Recommended')}
              className={`enhanced-category-tab recommended ${activeCategory === 'Recommended' ? 'active' : ''}`}
            >
              {t('aiIdeaGenerator.forYou')} <span className="category-count">{recommendedTypes.length}</span>
            </button>
          )}
          
          {CATEGORIES.map(category => {
            const count = PROBLEM_TYPES.filter(pt => pt.category === category).length;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`enhanced-category-tab ${activeCategory === category ? 'active' : ''}`}
              >
                {getCategoryName(category)} <span className="category-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Results Summary */}
      <div className="enhanced-results-summary">
        {t('aiIdeaGenerator.showing')} <span className="summary-highlight">{filteredProblemTypes.length}</span> {t('aiIdeaGenerator.problemTypes')}
        {activeCategory !== 'All' && ` ${t('aiIdeaGenerator.in')} ${getCategoryName(activeCategory)}`}
        {searchTerm && ` ${t('aiIdeaGenerator.matching')} "${searchTerm}"`}
        {multiSelect && (
          <> • <span className="summary-highlight">{selectedValues.length}/{maxSelections}</span> {t('aiIdeaGenerator.selected')}</>
        )}
      </div>

      {/* Enhanced Problem Grid */}
      <div className="enhanced-problems-grid">
        {filteredProblemTypes.map(problemType => {
          const isSelected = selectedValues.includes(problemType.id);
          const isRecommended = recommendedTypes.includes(problemType.id);
          const canSelect = multiSelect ? selectedValues.length < maxSelections || isSelected : true;
          
          return (
            <div
              key={problemType.id}
              className={`enhanced-problem-card ${isSelected ? 'selected' : ''} ${isRecommended ? 'recommended' : ''}`}
              onClick={() => canSelect && handleProblemTypeSelect(problemType.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && canSelect) {
                  e.preventDefault();
                  handleProblemTypeSelect(problemType.id);
                }
              }}
              onMouseEnter={(e) => showTooltip(
                `Examples: ${problemType.examples.join(', ')}. This falls under ${problemType.category}.`,
                e
              )}
              onMouseLeave={hideTooltip}
              aria-pressed={isSelected}
              style={{ cursor: canSelect ? 'pointer' : 'not-allowed', opacity: canSelect ? 1 : 0.6 }}
            >
              <div className="enhanced-problem-header">
                <div className="enhanced-problem-icon">{problemType.icon}</div>
                <div className="enhanced-problem-content">
                  <h4 className="enhanced-problem-title">{getProblemTitle(problemType.id)}</h4>
                  <p className="enhanced-problem-description">{getProblemDescription(problemType.id)}</p>
                </div>
              </div>
              
              <div className="enhanced-problem-examples">
                {problemType.examples.slice(0, 3).map((example, index) => (
                  <span key={index} className="enhanced-example-tag">
                    {getProblemExample(problemType.id, index)}
                  </span>
                ))}
                {problemType.examples.length > 3 && (
                  <span className="enhanced-example-tag">
                    +{problemType.examples.length - 3} {t('aiIdeaGenerator.more')}
                  </span>
                )}
              </div>
              
              <div className="enhanced-problem-footer">
                <span className="enhanced-category-badge">{getCategoryName(problemType.category)}</span>
                <div className="enhanced-selection-checkbox">
                  {isSelected && <span className="checkmark">✓</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* No Results State */}
      {filteredProblemTypes.length === 0 && (
        <div className="no-problems-found">
          <div className="no-problems-icon">🔍</div>
          <h4 className="no-problems-title">{t('aiIdeaGenerator.noProblemsFound')}</h4>
          <p className="no-problems-description">
            {t('aiIdeaGenerator.tryAdjustingSearch')}
          </p>
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setActiveCategory('All');
            }}
            className="reset-filters-btn"
          >
            {t('aiIdeaGenerator.resetFilters')}
          </button>
        </div>
      )}

      {/* Sticky CTA */}
      {multiSelect && (
        <div className="sticky-problem-cta">
          <div className="cta-selection-summary">
            <span className="cta-selection-count">{selectedValues.length}/{maxSelections}</span> {t('aiIdeaGenerator.problemAreasSelected')}
            {selectedValues.length > 0 && (
              <div className="problem-quick-actions">
                <button
                  type="button"
                  onClick={clearAllSelections}
                  className="quick-action-btn"
                >
                  {t('aiIdeaGenerator.clearAll')}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="enhanced-generate-btn"
            disabled={selectedValues.length === 0}
          >
            {t('aiIdeaGenerator.generateIdeas')}
            {selectedValues.length > 0 && (
              <span className="btn-count">({selectedValues.length})</span>
            )}
          </button>
        </div>
      )}

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="problem-tooltip visible"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translateX(-50%)'
          }}
        >
          {tooltip.content}
        </div>
      )}
    </div>
  );
};